import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { POSTS, type Post } from '../../../mocks/posts';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const foundPost = POSTS.find(p => p.slug === slug);
    setPost(foundPost || null);

    if (foundPost && foundPost.sections.length > 0) {
      setActiveSection(foundPost.sections[0].id);
    }

    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    const handleScroll = () => {
      const sections = post.sections.map(s => document.getElementById(s.id)).filter(Boolean);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(post.sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  const formatDate = (dateISO: string) => {
    const date = new Date(dateISO);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16 px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Artigo não encontrado.
            </h1>
            <Link
              to="/blog"
              className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 whitespace-nowrap"
            >
              Voltar para o Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        <article className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-8 text-sm text-gray-600">
              <Link to="/" className="hover:text-teal-600 transition-colors duration-150">
                Início
              </Link>
              <span className="mx-2">&gt;</span>
              <Link to="/blog" className="hover:text-teal-600 transition-colors duration-150">
                Blog
              </Link>
              <span className="mx-2">&gt;</span>
              <span className="text-gray-900">{post.title}</span>
            </nav>

            {/* Post Header */}
            <header className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8">
                <span>{formatDate(post.dateISO)}</span>
                <span>•</span>
                <span>Tempo de leitura: {post.readingTimeMinutes} min</span>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <p className="text-base text-gray-800 leading-relaxed">
                  <strong>Importante:</strong> Conteúdo informativo, sem promessa de resultado. Para orientação jurídica, é necessária análise do caso.
                </p>
              </div>
            </header>

            {/* Table of Contents */}
            <nav className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-12">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Neste artigo</h2>
              <ul className="space-y-2">
                {post.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`text-left w-full px-3 py-2 rounded-md transition-all duration-150 hover:bg-teal-50 hover:text-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 ${activeSection === section.id
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-gray-700'
                        }`}
                    >
                      {section.headingPtBr}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Post Content */}
            <div className="prose prose-lg max-w-none">
              {post.sections.map((section, index) => (
                <section key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    {section.headingPtBr}
                  </h2>

                  <p className="text-gray-700 leading-relaxed mb-6 text-base">
                    {section.bodyPtBr}
                  </p>

                  {section.bulletsPtBr && section.bulletsPtBr.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {section.bulletsPtBr.map((bullet, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-teal-600 mr-3 mt-1 flex-shrink-0">
                            <i className="ri-check-line text-xl"></i>
                          </span>
                          <span className="text-gray-700 text-base">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.faqPtBr && section.faqPtBr.length > 0 && (
                    <div className="space-y-6 mt-8">
                      {section.faqPtBr.map((faq, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <h3 className="text-lg font-bold text-gray-900 mb-3">
                            {faq.question}
                          </h3>
                          <p className="text-gray-700 leading-relaxed text-base">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Mid-article CTA (after 2nd section) */}
                  {index === 1 && (
                    <div className="my-12 bg-gradient-to-br from-teal-50 to-white border border-teal-200 rounded-lg p-8 text-center">
                      <p className="text-lg text-gray-800 mb-6">
                        Quer saber se o seu caso se enquadra? Fale no WhatsApp para uma avaliação inicial.
                      </p>
                      <a
                        href="https://wa.me/5544988019071?text=Olá! Gostaria de uma avaliação sobre meu caso previdenciário."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-8 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors duration-200 whitespace-nowrap"
                      >
                        Falar no WhatsApp
                      </a>
                    </div>
                  )}
                </section>
              ))}
            </div>

            {/* End CTA */}
            <div className="mt-16 bg-gradient-to-br from-teal-50 to-white border border-teal-200 rounded-lg p-8 text-center">
              <p className="text-lg text-gray-800 mb-6">
                Quer saber se o seu caso se enquadra? Fale no WhatsApp para uma avaliação inicial.
              </p>
              <a
                href="https://wa.me/5544988019071?text=Olá! Gostaria de uma avaliação sobre meu caso previdenciário."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors duration-200 whitespace-nowrap"
              >
                Falar no WhatsApp
              </a>
            </div>

            {/* Back to Blog Link */}
            <div className="mt-12 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors duration-150"
              >
                <i className="ri-arrow-left-line"></i>
                Voltar para o Blog
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
